import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { IoClose, IoSearchOutline, IoCaretDown } from 'react-icons/io5'
import { HiOutlineSortDescending } from 'react-icons/hi'
import { VscChecklist } from 'react-icons/vsc'

import { SelectMenu, Button, toaster } from 'evergreen-ui'
import DatePicker from 'react-datepicker'
import { format } from 'date-fns'

import {
    optionChoices,
    optionValueChoices,
    filterNames,
    getMeasurementTypeOptions
} from '../static/filterOptions'
import useTenantType from '../hooks/useTenantType'
import '../styles/components/activeFilters.scss'
import AddMeterDialog from './AddContentDialogs/AddMeterDialog'
import AddMeasurementDialog from './AddContentDialogs/AddMeasurementDialog'
import ExportToCsv from './ExportToCsv/ExportToCsv'

const Param = ({ name, value, onRemoveClick }) => {
    // TODO: find a better way to handle non-removable filters
    if (
        name === 'consumption_range' ||
        name === 'consumption_ts_after' ||
        name === 'consumption_ts_before'
    ) {
        return null
    }

    return (
        <div className="active_filter">
            <span>
                {filterNames[name] ?? 'Nieznany filtr: '}
                {optionValueChoices[value] ?? (value === 'undefined' ? 'nie znaleziono' : value)}
            </span>
            <button type="button" className="clearButton" onClick={() => onRemoveClick(name)}>
                <IoClose size={17} color="#FFF" />
            </button>
        </div>
    )
}

const FilterField = ({ name, options }) => {
    const [selectedValue, setSelectedValue] = useState(null)

    const [urlParams, setUrlParams] = useSearchParams()

    useEffect(() => {
        if (!urlParams.has(options[0].filter)) {
            setSelectedValue(null)
        }
        if (urlParams.has(options[0].filter)) {
            setSelectedValue(
                options.find((opt) => opt?.value === urlParams?.get(options?.[0]?.filter))
            )
        }
    }, [urlParams])
    const handleOptionClick = (item) => {
        setSelectedValue(item)

        urlParams.delete(item.filter)
        urlParams.append(item.filter, item.value)

        setUrlParams(urlParams)
    }

    return (
        <SelectMenu
            hasFilter={false}
            hasTitle={false}
            options={options}
            height={options.length * 33}
            onSelect={handleOptionClick}
            closeOnSelect
        >
            <Button className="option" iconAfter={IoCaretDown}>
                {selectedValue === null ? name : selectedValue.label}
            </Button>
        </SelectMenu>
    )
}

const FilterSearchField = ({ name }) => {
    const [value, setValue] = useState('')
    const [urlParams, setUrlParams] = useSearchParams()

    const handleConfirm = (text) => {
        urlParams.delete('search')
        urlParams.append('search', text)
        setUrlParams(urlParams)
    }

    const handleKeyDownPressed = (event) => {
        if (event.keyCode === 13) {
            if (value === '') {
                toaster.warning('Nie podano wartości')
            } else {
                handleConfirm(value)
            }
        }
    }

    const handleButtonClick = () => {
        if (value === '') {
            toaster.warning('Nie podano wartości')
        } else {
            handleConfirm(value)
        }
    }

    return (
        <div className="search_input">
            <input
                type="text"
                placeholder={name}
                onKeyDown={handleKeyDownPressed}
                onChange={(e) => setValue(e.target.value)}
            />
            <button type="button" className="clearButton" onClick={handleButtonClick}>
                <IoSearchOutline size="17" />
            </button>
        </div>
    )
}

const FilterDateField = ({ fromParam, toParam, type }) => {
    const [selectedValue, setSelectedValue] = useState(null)

    const [urlParams, setUrlParams] = useSearchParams()

    const handleDateClick = (date) => {
        setSelectedValue(date)

        if (type === 'month') {
            const startDate = new Date(date.getFullYear(), date.getMonth(), 1)

            urlParams.delete(fromParam)
            urlParams.append(fromParam, format(startDate, 'yyyy-MM-dd'))
            urlParams.delete(toParam)
            urlParams.append(toParam, format(startDate, 'yyyy-MM-dd'))
        }

        if (type === 'day') {
            const startDate = new Date(date.setHours(0, 0, 0, 0))

            urlParams.delete(fromParam)
            urlParams.append(fromParam, format(startDate, 'yyyy-MM-dd'))
            urlParams.delete(toParam)
            urlParams.append(toParam, format(startDate, 'yyyy-MM-dd'))
        }

        setUrlParams(urlParams)
    }

    useEffect(() => {
        if ((!urlParams.has(fromParam) && !urlParams.has(toParam)) || !selectedValue) {
            const now = new Date()
            handleDateClick(now)
        }
    }, [urlParams])

    return (
        <DatePicker
            customInput={
                <Button className="option" iconAfter={IoCaretDown}>
                    {selectedValue === null && 'Wybierz datę'}
                    {selectedValue !== null &&
                        (type === 'month'
                            ? format(new Date(selectedValue), 'MM-yyyy')
                            : format(new Date(selectedValue), 'dd-MM-yyyy'))}
                </Button>
            }
            selected={selectedValue}
            onChange={handleDateClick}
            locale="pl"
            dateFormat="dd-MM-yyyy"
            showPopperArrow={false}
            showMonthYearPicker={type === 'month'}
        />
    )
}

const ActiveFilters = ({
    onRemoveClick = () => {},
    userFilters = false,
    measurementFilters = false,
    meterFilters = false,
    documentFilters = false,
    notificationFilters = false,
    historyFilters = false,
    consumptionFilters = false,
    onMassActionSelect,
    onExportClick = () => {}
}) => {
    const isModerator = useSelector((state) => state.profile.profile.isModerator)
    const { isInstitutionalMonitoring } = useTenantType()

    const [urlParams, setUrlParams] = useSearchParams()
    const [entries, setEntries] = useState({})
    const [massActionOptions, setMassActionOptions] = useState([])
    const [sortOptions, setSortOptions] = useState([])
    const [selectedSorting, setSelectedSorting] = useState(null)

    const [showAddMeterDialog, setShowAddMeterDialog] = useState(false)
    const [showAddUserDialog, setShowAddUserDialog] = useState(false)
    const [showAddMeasurementDialog, setShowAddMeasurementDialog] = useState(false)

    const handleRemoveParamClick = (key) => {
        urlParams.delete(key)
        setUrlParams(urlParams)
        onRemoveClick()
    }

    const handleSelectSortingClick = (item) => {
        urlParams.delete(item.sorting)
        if (item.value !== 'default') {
            urlParams.append(item.sorting, item.value)
            setSelectedSorting(item.label)
        } else setSelectedSorting(null)
        setUrlParams(urlParams)
    }

    useEffect(() => {
        setEntries(
            Object.fromEntries([...urlParams.entries()].filter((elem) => elem[0] !== 'sorting'))
        )
    }, [urlParams])

    useEffect(() => {
        if (userFilters) {
            setMassActionOptions(optionChoices.userMassActionOptions)
            setSortOptions(optionChoices.userSortOptions)
        } else if (meterFilters) {
            setMassActionOptions(optionChoices.meterMassActionOptions)
            setSortOptions(optionChoices.meterSortOptions)
        } else if (measurementFilters) {
            setMassActionOptions(optionChoices.measuerementMassActionOptions)
            setSortOptions(optionChoices.measuerementSortOptions)
        } else if (documentFilters) {
            setMassActionOptions(optionChoices.documentMassActionOptions)
            setSortOptions(optionChoices.documentSortOptions)
        } else if (notificationFilters) {
            setSortOptions(optionChoices.notificationSortOptions)
        } else if (historyFilters) {
            setSortOptions(optionChoices.historySortOptions)
        } else if (consumptionFilters) {
            setSortOptions(optionChoices.consumptionSortOptions)
        }
    }, [])

    const renderFilters = () => {
        return Object.entries(entries).map(([key, value]) => (
            <Param key={key} name={key} value={value} onRemoveClick={handleRemoveParamClick} />
        ))
    }

    const checkActivities = isModerator
        ? !notificationFilters &&
          !historyFilters &&
          !measurementFilters &&
          !consumptionFilters &&
          !meterFilters &&
          !userFilters
        : !notificationFilters &&
          !historyFilters &&
          !measurementFilters &&
          !consumptionFilters &&
          !meterFilters &&
          !userFilters

    return (
        <div>
            <div className="filters_container">
                <div id="actions">
                    <div>
                        {checkActivities && (
                            <SelectMenu
                                hasFilter={false}
                                hasTitle={false}
                                options={massActionOptions}
                                height={massActionOptions.length * 33}
                                onSelect={onMassActionSelect}
                                closeOnSelect
                            >
                                <Button className="option" iconBefore={VscChecklist}>
                                    Działania
                                </Button>
                            </SelectMenu>
                        )}

                        <SelectMenu
                            hasFilter={false}
                            hasTitle={false}
                            options={sortOptions}
                            height={sortOptions.length * 33}
                            onSelect={handleSelectSortingClick}
                            closeOnSelect
                        >
                            <Button
                                className="option"
                                iconBefore={HiOutlineSortDescending}
                                iconAfter={IoCaretDown}
                            >
                                {selectedSorting === null ? 'Sortowanie' : selectedSorting}
                            </Button>
                        </SelectMenu>
                    </div>
                </div>
                {userFilters && (
                    <div className="filters_row">
                        <div className="choose_row">
                            {!isInstitutionalMonitoring && (
                                <FilterField
                                    name="Stan weryfikacji"
                                    options={optionChoices.userStateOptions}
                                />
                            )}
                            {isModerator && !isInstitutionalMonitoring && (
                                <FilterField
                                    name="Typ odbiorcy"
                                    options={optionChoices.userTypeOptions}
                                />
                            )}
                            {isModerator && (
                                <FilterField
                                    name="Data utworzenia"
                                    options={optionChoices.userCreatedAtOptions}
                                />
                            )}
                            {isModerator && (
                                <FilterField
                                    name="Data modyfikacji"
                                    options={optionChoices.userUpdatedAtOptions}
                                />
                            )}
                        </div>
                        <div className="search_row">
                            {/* TODO: disabled temporarily until the function is confirmed to work properly */}
                            {false && isModerator && (
                                <Button
                                    onClick={() => setShowAddMeterDialog(true)}
                                    className="button"
                                >
                                    Dodaj odbiorcę
                                </Button>
                            )}
                            <FilterSearchField
                                name={
                                    isInstitutionalMonitoring
                                        ? 'Szukaj lokalizacji...'
                                        : 'Szukaj odbiorcy...'
                                }
                            />
                        </div>
                    </div>
                )}
                {measurementFilters && (
                    <div className="filters_row">
                        <div className="choose_row">
                            {!isModerator && (
                                <FilterField
                                    name="Stan weryfikacji"
                                    options={optionChoices.measurementStateOptions}
                                />
                            )}
                            <FilterField
                                name={isInstitutionalMonitoring ? 'Typ pomiaru' : 'Typ licznika'}
                                options={getMeasurementTypeOptions(isInstitutionalMonitoring)}
                            />
                            {isModerator && (
                                <FilterField
                                    name="Data utworzenia"
                                    options={optionChoices.measurementCreatedAtOptions}
                                />
                            )}
                        </div>
                        <div className="search_row">
                            {/* TODO: disabled temporarily until the function is confirmed to work properly */}
                            {false && (
                                <Button
                                    onClick={() => setShowAddMeasurementDialog(true)}
                                    className="button"
                                >
                                    Dodaj odczyt
                                </Button>
                            )}
                            <FilterSearchField
                                name={
                                    isInstitutionalMonitoring
                                        ? 'Szukaj pomiaru...'
                                        : 'Szukaj odczytu...'
                                }
                            />
                        </div>
                    </div>
                )}
                {consumptionFilters && (
                    <div className="filters_row">
                        <div className="choose_row">
                            <FilterField
                                name={isInstitutionalMonitoring ? 'Typ pomiaru' : 'Typ licznika'}
                                options={getMeasurementTypeOptions(isInstitutionalMonitoring)}
                            />
                            <FilterField
                                name="Zakres"
                                options={optionChoices.consumptionTimeRangeOptions}
                            />
                            {urlParams.get('consumption_range') === 'day' && (
                                <FilterDateField
                                    fromParam="consumption_ts_after"
                                    toParam="consumption_ts_before"
                                    type="day"
                                />
                            )}
                            {urlParams.get('consumption_range') === 'month' && (
                                <FilterDateField
                                    fromParam="consumption_ts_after"
                                    toParam="consumption_ts_before"
                                    type="month"
                                />
                            )}
                        </div>
                        <div className="search_row">
                            <ExportToCsv onExport={onExportClick} />
                            <FilterSearchField name="Szukaj zużycia..." />
                        </div>
                    </div>
                )}
                {meterFilters && (
                    <div className="filters_row">
                        <div className="choose_row">
                            <FilterField
                                name={isInstitutionalMonitoring ? 'Typ pomiaru' : 'Typ licznika'}
                                options={getMeasurementTypeOptions(isInstitutionalMonitoring)}
                            />
                            {isModerator && (
                                <FilterField
                                    name="Data ostatniego odczytu"
                                    options={optionChoices.meterUpdatedAtOptions}
                                />
                            )}
                        </div>
                        <div className="search_row">
                            {/* TODO: disabled temporarily until the function is confirmed to work properly */}
                            {false && (
                                <Button
                                    onClick={() => setShowAddMeterDialog(true)}
                                    className="button"
                                >
                                    Dodaj licznik
                                </Button>
                            )}
                            <FilterSearchField
                                name={
                                    isInstitutionalMonitoring
                                        ? 'Szukaj urządzenia...'
                                        : 'Szukaj licznika...'
                                }
                            />
                        </div>
                    </div>
                )}
                {documentFilters && (
                    <div className="filters_row">
                        <div className="choose_row">
                            {isModerator && (
                                <FilterField
                                    name="Data utworzenia"
                                    options={optionChoices.documentCreatedAtOptions}
                                />
                            )}
                            <FilterField
                                name="Płatności"
                                options={optionChoices.documentPaymentsOptions}
                            />
                        </div>
                    </div>
                )}
                {notificationFilters && (
                    <div className="filters_row">
                        <div className="choose_row">
                            <FilterField
                                name="Wysłane automatycznie"
                                options={optionChoices.notificationIsAutomaticOptions}
                            />
                            <FilterField
                                name="Kanał"
                                options={optionChoices.notificationChannelOptions}
                            />
                            <FilterField
                                name="Odbiorca"
                                options={optionChoices.notificationRecipientOptions}
                            />
                            <FilterField
                                name="Data utworzenia"
                                options={optionChoices.notificationCreatedAtOptions}
                            />
                        </div>
                    </div>
                )}
                {historyFilters && (
                    <div className="filters_row">
                        <div className="choose_row">
                            <FilterField
                                name="Wykluczenie"
                                options={optionChoices.historyExcludeOptions}
                            />
                            {isModerator && (
                                <FilterField
                                    name="Data utworzenia"
                                    options={optionChoices.historyCreatedAtOptions}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
            <div className="filters_container">
                {Object.keys(entries).length === 0 ? null : <>{renderFilters()}</>}
            </div>
            <AddMeterDialog
                isShown={showAddMeterDialog}
                setIsShown={() => setShowAddMeterDialog(false)}
            />
            <AddMeasurementDialog
                isShown={showAddMeasurementDialog}
                setIsShown={() => setShowAddMeasurementDialog(false)}
            />
        </div>
    )
}

export default ActiveFilters
